import { integerToMoney } from '@/app/lib/moneyHandler'
import { Slider } from '@mui/material'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { useRange, UseRangeProps } from 'react-instantsearch'

type RangeSliderProps = UseRangeProps & {
  formatLabel?: (value: number) => string | number
  className?: string
}

export function RangeSlider({
  formatLabel = integerToMoney,
  className,
  ...rangeProps
}: RangeSliderProps) {
  const t = useTranslations('CustomRangeRefinement')
  const { start, range, canRefine, refine } = useRange({
    ...rangeProps,
  })

  const [value, setValue] = useState<[number, number]>([
    range.min ?? 0,
    range.max ?? 100,
  ])

  const handleChange = (_event: Event, newValue: number | number[]) => {
    setValue(newValue as [number, number])
  }

  const handleChangeCommitted = (
    _event: Event | React.SyntheticEvent,
    newValue: number | number[]
  ) => {
    const [min, max] = newValue as [number, number]
    refine([min, max])
  }

  const hasValidRange = range.min !== undefined && range.max !== undefined

  useEffect(() => {
    const minValue =
      start[0] !== undefined && start[0] !== -Infinity
        ? start[0]
        : (range.min ?? 0)
    const maxValue =
      start[1] !== undefined && start[1] !== Infinity
        ? start[1]
        : (range.max ?? 100)

    setValue([minValue, maxValue])
  }, [start, range])

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="px-2">
        <Slider
          value={value}
          min={range.min ?? 0}
          max={range.max ?? 100}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
          valueLabelDisplay="auto"
          valueLabelFormat={formatLabel}
          disabled={!canRefine || !hasValidRange}
          disableSwap
          classes={{
            root: 'text-mydra-purple h-1',
            thumb: cn(
              'w-4 h-4 bg-white border-2 border-current',
              'hover:shadow-[0_0_0_8px_rgba(137,90,215,0.16)]',
              '[&.Mui-active]:shadow-[0_0_0_14px_rgba(137,90,215,0.16)]',
              '[&.Mui-focusVisible]:shadow-[0_0_0_8px_rgba(137,90,215,0.16)]'
            ),
            valueLabel: cn(
              'text-xs font-normal -top-1.5',
              'bg-mydra-purple rounded',
              'before:hidden'
            ),
            track: 'border-none h-1',
            rail: 'opacity-30 bg-lines h-1',
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-2 text-sm">
        <span className={cn('text-mydra-black', 'font-normal')}>
          {formatLabel(value[0])}
        </span>
        <span className={cn('text-gray-500', 'font-normal')}>{t('to')}</span>
        <span className={cn('text-mydra-black', 'font-normal')}>
          {formatLabel(value[1])}
        </span>
      </div>
    </div>
  )
}

export function useRangeRefinementStatus(rangeProps: UseRangeProps) {
  const { start, range, canRefine } = useRange(rangeProps)

  const hasValidRange = range.min !== undefined && range.max !== undefined

  const hasRefinedRange =
    (start[0] !== undefined &&
      start[0] !== -Infinity &&
      start[0] !== range.min) ||
    (start[1] !== undefined && start[1] !== Infinity && start[1] !== range.max)

  return {
    canRefine,
    hasValidRange,
    hasRefinedRange,
  }
}
